import { canAccessModule } from "@clientflow/shared";
import { hasPermission, type Me } from "./session";

/**
 * Firm Settings' groups and tabs, and who may open each one (the API enforces the same). Kept apart
 * from the page so the rules can be tested: a tab a person can't open says so instead of quietly
 * showing another tab.
 */
export const SETTINGS_GROUPS = [
  {
    key: "team",
    label: "Team",
    tabs: [
      { key: "team", label: "Team and access" },
      { key: "time", label: "Time & costs" },
    ],
  },
  {
    key: "services",
    label: "Services and workflows",
    tabs: [
      { key: "workflows", label: "Workflow templates" },
      { key: "planning", label: "Tax planning" },
      { key: "compliance-rules", label: "Compliance rules" },
      { key: "cfo", label: "CFO benchmarks" },
      { key: "proposals", label: "Proposals and billing" },
      { key: "firm", label: "Firm preferences" },
    ],
  },
  {
    key: "clients",
    label: "Client communication",
    tabs: [
      { key: "booking", label: "Booking" },
      { key: "leads", label: "Leads" },
      { key: "reviews", label: "Review requests" },
    ],
  },
  {
    key: "data",
    label: "Data and security",
    tabs: [
      { key: "data", label: "Import & export" },
      { key: "ai", label: "AI" },
      { key: "storage", label: "Documents & retention" },
      { key: "backups", label: "Backups" },
      { key: "rules", label: "Rules and rates" },
      { key: "audit", label: "Audit log" },
    ],
  },
  {
    // Every outside service on one page. Owners and admins see everything; staff their own connections,
    // their clients' QuickBooks (with Accounting Services access) and the lead links.
    key: "integrations",
    label: "Integrations",
    tabs: [
      { key: "integrations", label: "Services" },
      { key: "integrations-staff", label: "Staff connections" },
      { key: "integrations-clients", label: "Client QuickBooks" },
      { key: "integrations-leads", label: "Leads and client import" },
      { key: "integrations-firm-qbo", label: "The firm's QuickBooks" },
    ],
  },
] as const;

export type SettingsTabKey = (typeof SETTINGS_GROUPS)[number]["tabs"][number]["key"];
export const SETTINGS_TAB_KEYS: SettingsTabKey[] = SETTINGS_GROUPS.flatMap((g) => g.tabs.map((x) => x.key));

/** Whether this person may see each Integrations tab (the API enforces the same). */
export function integrationTabs(me: Me) {
  const manage = hasPermission(me, "integrations.manage");
  const books = me.role === "owner" || me.role === "admin" || (me.staffModules ?? []).includes("bookkeeping");
  return { manage, books };
}

/** Which Firm Settings tabs this person may open. */
export function settingsTabAccess(me: Me): Record<SettingsTabKey, boolean> {
  const integrations = integrationTabs(me);
  const tax = canAccessModule(me.role, me.staffModules, "tax");
  const books = canAccessModule(me.role, me.staffModules, "bookkeeping");
  return {
    team: true,
    time: hasPermission(me, "firm.manage"),
    workflows: true,
    planning: true,
    "compliance-rules": tax || books,
    cfo: hasPermission(me, "clients.read"),
    proposals: hasPermission(me, "billing.read"),
    firm: true,
    booking: true,
    leads: true,
    reviews: true,
    data: true,
    ai: true,
    storage: hasPermission(me, "documents.read"),
    backups: hasPermission(me, "backups.read"),
    rules: hasPermission(me, "clients.read"),
    audit: hasPermission(me, "audit.read"),
    integrations: integrations.manage,
    "integrations-staff": true,
    "integrations-clients": integrations.books,
    "integrations-leads": true,
    "integrations-firm-qbo": integrations.manage,
  };
}

export const SETTINGS_NO_ACCESS = "You don't have access to this part of Firm Settings";

/**
 * The tab `?tab=` asks for. A tab the person can't open is `denied` (the page says so, and names its
 * group); an unknown or missing key opens Team and access, as before.
 */
export function resolveSettingsTab(access: Record<SettingsTabKey, boolean>, requested: string | null): { tab: SettingsTabKey; denied: boolean; groupKey: string } {
  const groupOf = (k: SettingsTabKey) => SETTINGS_GROUPS.find((g) => g.tabs.some((x) => x.key === k))!.key;
  const known = SETTINGS_TAB_KEYS.find((k) => k === requested);
  if (!known) return { tab: "team", denied: false, groupKey: "team" };
  if (!access[known]) {
    // Show the group the tab belongs to when the person may open something in it.
    const g = SETTINGS_GROUPS.find((x) => x.key === groupOf(known))!;
    return { tab: known, denied: true, groupKey: g.tabs.some((x) => access[x.key]) ? g.key : "team" };
  }
  return { tab: known, denied: false, groupKey: groupOf(known) };
}
